/**
 * Reset Password Page
 * Uses Supabase Auth to update password after reset link
 */
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthLayout } from '../components/auth/AuthLayout';
import { PasswordStrengthIndicator } from '../components/auth/PasswordStrengthIndicator';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { validatePassword } from '../utils/validation';
import { Eye, EyeOff, Loader2, AlertCircle, CheckCircle, ArrowLeft } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const ResetPassword = () => {
  const navigate = useNavigate();
  const { updatePassword, session } = useAuth();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState<{ password?: string; confirmPassword?: string }>({});
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [tokenValid, setTokenValid] = useState(true);

  useEffect(() => {
    // Supabase handles token validation via the URL hash
    // If we have a session after redirect, the token was valid
    if (!session && !window.location.hash.includes('access_token')) {
      // Give a moment for the auth state to settle
      const timer = setTimeout(() => {
        if (!session) setTokenValid(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [session]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrors({});
    setErrorMessage('');

    const newErrors: { password?: string; confirmPassword?: string } = {};
    if (!password) {
      newErrors.password = 'Password is required';
    } else if (!validatePassword(password)) {
      newErrors.password =
        'Password must be at least 8 characters with uppercase, lowercase, number, and special character';
    }
    if (!confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (password !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsLoading(true);
    try {
      const result = await updatePassword(password);
      if (result.success) {
        setSuccess(true);
        setTimeout(() => navigate('/app/login'), 3000);
      } else {
        setErrorMessage(result.error || 'Failed to reset password');
      }
    } catch (_error: unknown) {
      setErrorMessage('Failed to reset password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (!tokenValid) {
    return (
      <AuthLayout showSidePanel={false}>
        <Card
          className="w-full border-slate-200 shadow-lg rounded-2xl"
          data-testid="invalid-token-card"
        >
          <CardContent className="pt-8 pb-8">
            <div className="text-center">
              <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-slate-800 mb-2">Invalid or Expired Link</h2>
              <p className="text-slate-600 mb-6">
                This password reset link is invalid or has expired.
              </p>
              <Link to="/app/forgot-password">
                <Button data-testid="request-new-link-btn">Request a new link</Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout showSidePanel={false}>
      <div className="text-center mb-8">
        <h1
          className="text-4xl font-bold text-slate-800"
          style={{ fontFamily: 'Space Grotesk, sans-serif' }}
        >
          UserLife JML
        </h1>
      </div>

      <Card
        className="w-full border-slate-200 shadow-lg rounded-2xl"
        data-testid="reset-password-card"
      >
        <CardHeader className="space-y-1 pb-6">
          <CardTitle
            className="text-2xl sm:text-3xl font-semibold text-slate-800"
            style={{ fontFamily: 'Space Grotesk, sans-serif' }}
          >
            Set new password
          </CardTitle>
          <CardDescription className="text-base text-slate-600">
            Choose a strong password for your account
          </CardDescription>
        </CardHeader>

        <CardContent>
          {success && (
            <Alert
              className="mb-6 border-emerald-200 bg-emerald-50 text-emerald-800"
              data-testid="success-alert"
            >
              <CheckCircle className="h-4 w-4" />
              <AlertTitle>Password Updated</AlertTitle>
              <AlertDescription>
                Your password has been reset successfully. Redirecting to login...
              </AlertDescription>
            </Alert>
          )}

          {errorMessage && (
            <Alert variant="destructive" className="mb-6" data-testid="error-alert">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{errorMessage}</AlertDescription>
            </Alert>
          )}

          {!success && (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-medium text-slate-700">
                  New Password
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter new password"
                    value={password}
                    onChange={(e: any) => setPassword(e.target.value)}
                    className="h-11 pr-10 border-slate-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 rounded-lg transition-all duration-200"
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors duration-200"
                    data-testid="password-toggle-button"
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
                <PasswordStrengthIndicator password={password} />
                {errors.password && (
                  <p
                    className="text-sm text-red-600 flex items-center gap-1 mt-1"
                    data-testid="password-error"
                  >
                    <AlertCircle size={16} />
                    {errors.password}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-sm font-medium text-slate-700">
                  Confirm New Password
                </Label>
                <div className="relative">
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder="Confirm new password"
                    value={confirmPassword}
                    onChange={(e: any) => setConfirmPassword(e.target.value)}
                    className="h-11 pr-10 border-slate-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 rounded-lg transition-all duration-200"
                    data-testid="confirm-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors duration-200"
                    data-testid="confirm-password-toggle-button"
                  >
                    {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
                {errors.confirmPassword && (
                  <p
                    className="text-sm text-red-600 flex items-center gap-1 mt-1"
                    data-testid="confirm-password-error"
                  >
                    <AlertCircle size={16} />
                    {errors.confirmPassword}
                  </p>
                )}
              </div>

              <Button
                type="submit"
                disabled={isLoading}
                className="w-full h-11 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg shadow-sm hover:shadow-md transition-all duration-200"
                data-testid="submit-button"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    <span>Resetting...</span>
                  </>
                ) : (
                  'Reset Password'
                )}
              </Button>
            </form>
          )}

          <div className="mt-6">
            <Link
              to="/app/login"
              className="flex items-center justify-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-700 hover:underline"
              data-testid="back-to-login-link"
            >
              <ArrowLeft size={16} /> Back to login
            </Link>
          </div>
        </CardContent>
      </Card>
    </AuthLayout>
  );
};

export default ResetPassword;
